import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { SearchIcon } from '@slite/icons'

import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data, SuperWorkflowListItem } from './+data'
import { WorkflowCard } from './WorkflowCard'

const ALL = 'all'

export default function WorkflowsIndexPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { workflows, personas, featuredWorkflows } = useData<Data>()

  const [selectedPersona, setSelectedPersona] = useState<string>(ALL)
  const [searchQuery, setSearchQuery] = useState('')

  const title = intl.formatMessage({
    id: 'workflows.index.title',
    defaultMessage: 'Agent workflows',
  })
  const description = intl.formatMessage({
    id: 'workflows.index.subtitle',
    defaultMessage:
      'Ready-made agent workflows that save your team hours every week.',
  })

  const filteredWorkflows = useMemo<SuperWorkflowListItem[]>(() => {
    let results = workflows

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      results = results.filter(
        (workflow) =>
          workflow.title.toLowerCase().includes(query) ||
          (workflow.description ?? '').toLowerCase().includes(query)
      )
    } else if (selectedPersona !== ALL) {
      results = results.filter((workflow) =>
        workflow.personas?.some((persona) => persona.name === selectedPersona)
      )
    }

    return results
  }, [workflows, searchQuery, selectedPersona])

  const isFiltering = searchQuery.trim().length > 0 || selectedPersona !== ALL
  const showFeatured = !isFiltering && featuredWorkflows.length > 0

  const handlePersonaChange = (persona: string) => {
    setSelectedPersona(persona)
    setSearchQuery('')
  }

  const filterOptions = [
    {
      value: ALL,
      label: intl.formatMessage({
        id: 'workflows.sidebar.all',
        defaultMessage: 'All workflows',
      }),
    },
    ...personas.map((name) => ({ value: name, label: name })),
  ]

  return (
    <SlitePage
      title={title}
      description={description}
      canonicalUrl={routes.workflows.index()}
      background="moon-dust-lighter"
    >
      <Section background="moon-dust-lighter" className="text-align-center">
        <div className="grid-12">
          <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <WebsiteText
              variant="display"
              htmlTag="h1"
              className="margin-bottom-16"
            >
              {title}
            </WebsiteText>
            <WebsiteText
              variant="body"
              className="font-weight-semibold margin-bottom-32"
              style={{ maxWidth: 340 }}
            >
              {description}
            </WebsiteText>

            <div
              className="position-relative width-full"
              style={{ maxWidth: 360 }}
            >
              <input
                aria-label={intl.formatMessage({
                  defaultMessage: 'Search workflows',
                  id: 'workflows.index.search.ariaLabel',
                })}
                type="text"
                placeholder={intl.formatMessage({
                  defaultMessage: 'Search',
                  id: 'workflows.index.search.placeholder',
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={classNames(
                  'width-full border-small radius-largest padding-vertical-20 font-size-5',
                  searchQuery
                    ? 'background-white border-shade-05'
                    : 'background-transparent border-alpha-shade-40'
                )}
                style={{ paddingLeft: 24, paddingRight: 56 }}
              />
              <SearchIcon
                size={20}
                className="position-absolute shade-30"
                style={{ right: 28, top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          </div>
        </div>
      </Section>

      {showFeatured && (
        <Section background="moon-dust-lighter" containerTopPadding={0}>
          <div className="grid-12 grid-gap-40">
            <div className="grid-span-3 display-none-small">
              <span className="font-size-9 font-family-system font-weight-semibold shade-20 padding-horizontal-16">
                <FormattedMessage
                  id="workflows.index.featured"
                  defaultMessage="Featured"
                />
              </span>
            </div>
            <div className="grid-span-9 grid-12 grid-align-start grid-gap-24 grid-gap-12-small grid-span-12-small">
              {featuredWorkflows.map((workflow) => (
                <WorkflowCard
                  key={workflow._id}
                  slug={workflow.slug.current}
                  title={workflow.title}
                  estMinSaved={workflow.estMinSaved}
                  sources={workflow.sources}
                  size="large"
                  className="grid-span-6 grid-span-12-small"
                />
              ))}
            </div>
          </div>
        </Section>
      )}

      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <div className="grid-12 grid-gap-40">
          <div className="grid-span-3 display-none-small">
            <div
              className="flexbox flexbox-column flexbox-align-start position-sticky"
              style={{ top: 100 }}
            >
              {filterOptions.map((option) => (
                <a
                  key={option.value}
                  onClick={(e) => {
                    e.preventDefault()
                    handlePersonaChange(option.value)
                  }}
                  className={classNames(
                    'text-align-left font-size-9 font-family-system padding-vertical-8 padding-horizontal-16 margin-bottom-4 radius-largest pointer transition font-weight-semibold',
                    selectedPersona === option.value
                      ? 'background-moon-dust-darker shade-05 shade-05-on-hover'
                      : 'background-transparent shade-10 shade-20-on-hover'
                  )}
                >
                  {option.label}
                </a>
              ))}
            </div>
          </div>
          <div className="grid-span-9 grid-12 grid-gap-24 grid-gap-12-small grid-span-12-small">
            {filteredWorkflows.length === 0 ? (
              <div className="grid-span-12 text-align-center padding-vertical-48">
                <WebsiteText variant="body" className="shade-20">
                  <FormattedMessage
                    defaultMessage="No workflows match your filters."
                    id="workflows.index.no-results"
                  />
                </WebsiteText>
              </div>
            ) : (
              filteredWorkflows.map((workflow) => (
                <WorkflowCard
                  key={workflow._id}
                  slug={workflow.slug.current}
                  title={workflow.title}
                  estMinSaved={workflow.estMinSaved}
                  sources={workflow.sources}
                  className="grid-span-4 grid-span-6-medium grid-span-12-small"
                />
              ))
            )}
          </div>
        </div>
      </Section>
    </SlitePage>
  )
}
